import {
    Avatar, Box, Chip, Divider, IconButton, List, ListItem,
    ListItemButton, ListItemContent, ListItemDecorator,
    Sheet, Stack, Typography, Badge, Tooltip
} from "@mui/joy";

import DashboardRoundedIcon      from '@mui/icons-material/DashboardRounded';
import ViewKanbanRoundedIcon      from '@mui/icons-material/ViewKanbanRounded';
import CalendarMonthRoundedIcon   from '@mui/icons-material/CalendarMonthRounded';
import AssessmentRoundedIcon      from '@mui/icons-material/AssessmentRounded';
import InsertCommentRoundedIcon   from '@mui/icons-material/InsertCommentRounded';
import AccessibilityNewRoundedIcon from '@mui/icons-material/AccessibilityNewRounded';
import BookRoundedIcon            from '@mui/icons-material/BookRounded';
import CreditCardRoundedIcon      from '@mui/icons-material/CreditCardRounded';
import ContactSupportRoundedIcon  from '@mui/icons-material/ContactSupportRounded';
import ExitToAppRoundedIcon       from '@mui/icons-material/ExitToAppRounded';
import EditNoteRoundedIcon        from '@mui/icons-material/EditNoteRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SafeIcon from '../SafeIcon';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';

import { motion, AnimatePresence } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { useLocation, useNavigate } from "react-router";
import { closeSidebar } from "../../utils";
import { BASE_URL } from "../../api";
import { useWindowWidth } from "../../hooks";
import { Icon as MeetIcon } from '../../moonmeet/UI';
import BrandLogo from "../BrandLogo";


// ─── Nav items config ─────────────────────────────────────────────────────────
const NAV_MAIN = [
    { label: "Дашборд", icon: <DashboardRoundedIcon />, path: "/" },
    { label: "Канбан", icon: <ViewKanbanRoundedIcon />, path: "/kanban" },
    { label: "Заметки", icon: <EditNoteRoundedIcon />, path: "/notes" },
    { label: "Задачи", icon: <ListAltRoundedIcon />, path: "/tasks" },
    { label: "Финансы", icon: <CreditCardRoundedIcon />, path: "/finances" },


    // { label: "Календарь", icon: <CalendarMonthRoundedIcon />, path: "/calendar" },
    // { label: "Аналитика", icon: <AssessmentRoundedIcon />, path: "/statistics" },
    // { label: "Платежи", icon: <CreditCardRoundedIcon />, path: "/payments" },
];

const NAV_APPS = [
    { label: "Magic Safe", icon: <SafeIcon />, path: "/safe" },
    { label: "Drawer", icon: <DrawOutlinedIcon />, path: "/drawer" },
    { label: "MoonMeet", icon: <MeetIcon name="camera"/>, path: "/moonmeet" },
];

const SHOW_TEAM = false;
const NAV_SECONDARY = [
    { label: "Исполнители", icon: <AccessibilityNewRoundedIcon />,    path: "/performers" },
    { label: "Чаты",        icon: <InsertCommentRoundedIcon />,       path: "/chats", badge: true },
    { label: "База знаний", icon: <BookRoundedIcon />,                path: "/knowledge" },
];

// ─── NavItem ──────────────────────────────────────────────────────────────────
function NavItem({ item, active, onClick, badgeCount }) {
    return (
        <ListItem sx={{ p: 0 }}>
            <ListItemButton
                onClick={onClick}
                selected={active}
                sx={{
                    borderRadius: "10px",
                    px: 1.5,
                    py: 0.9,
                    gap: 1.5,
                    transition: "all 0.15s ease",
                    color: active ? "primary.plainColor" : "text.secondary",
                    fontWeight: active ? 600 : 400,
                    bgcolor: active ? "primary.softBg" : "transparent",
                    "&:hover": {
                        bgcolor: active ? "primary.softHoverBg" : "neutral.softBg",
                        color: "text.primary",
                    },
                    "& .MuiSvgIcon-root": {
                        fontSize: 18,
                        opacity: active ? 1 : 0.6,
                        color: active ? "primary.plainColor" : "inherit",
                    },
                }}
            >
                <ListItemDecorator sx={{ minWidth: 0, mr: 0 }}>
                    {item.icon}
                </ListItemDecorator>
                <ListItemContent>
                    <Typography
                        level="body-sm"
                        sx={{ fontWeight: "inherit", color: "inherit", lineHeight: 1 }}
                    >
                        {item.label}
                    </Typography>
                </ListItemContent>
                {badgeCount > 0 && (
                    <Chip
                        size="sm"
                        color="danger"
                        variant="solid"
                        sx={{ fontSize: "0.65rem", minWidth: 20, height: 20, borderRadius: "99px", px: 0.6 }}
                    >
                        {badgeCount}
                    </Chip>
                )}
            </ListItemButton>
        </ListItem>
    );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
export default function Sidebar({ compact = false, onExpand }) {
    const {
        sidebarOpen, setSidebarOpen,
        profile,
        logout,
        totalUnread, setModalProfile,
    } = useContext(LayoutContext);

    const navigate  = useNavigate();
    const location  = useLocation();

    function clsBar() {
        closeSidebar();
        setSidebarOpen(false);
    }

    function handleNav(path) {
        if (location.pathname !== path) navigate(path);
        clsBar();
    }

    const windowWidth = useWindowWidth();

    // При расширении до десктопа — закрываем мобайл-сайдбар
    useEffect(() => {
        if (windowWidth >= 900) {
            if (sidebarOpen) {
                closeSidebar();
                setSidebarOpen(false);
            }
        }
    }, [windowWidth]);

    // function handleExit() {
    //     try {
    //         // Cookies.remove("auth_token");
    //         // Cookies.remove("auth_token_employee");
    //     } catch {}
    //     setLock?.(true);
    //     setAccount?.({});
    //     setTokenEmployee?.(null);
    // }

    if (compact && windowWidth >= 900) return <Sheet data-sidebar-compact="true" component={motion.aside} initial={false} animate={{ width: 68 }}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1.5, px: .5, m: 1, mr: 0, borderRadius: 20, flexShrink: 0, minHeight: 0, overflowY: 'auto', bgcolor: 'var(--ld-sidebar)' }}>
        <Tooltip title="Обычный режим LunaDesk" placement="right"><IconButton aria-label="Обычный режим LunaDesk" onClick={onExpand} sx={{ p: 0, mb: 1 }}><BrandLogo/></IconButton></Tooltip>
        {[NAV_MAIN, NAV_APPS, ...(SHOW_TEAM ? [NAV_SECONDARY] : [])].map((group, index) => <Box key={index} sx={{ width: '100%' }}>
            {index > 0 && <Divider sx={{ my: 1, mx: 1 }}/>}
            {group.map(item => <Tooltip key={item.path} title={item.label} placement="right"><IconButton aria-label={item.label} onClick={() => handleNav(item.path)} variant={location.pathname === item.path ? 'soft' : 'plain'} color={location.pathname === item.path ? 'primary' : 'neutral'}
                sx={{ display: 'flex', mx: 'auto', my: .4, width: 42, height: 42, borderRadius: 12, '& svg': { fontSize: 20 } }}>{item.icon}</IconButton></Tooltip>)}
        </Box>)}
        <Box sx={{ flex: 1 }}/><Tooltip title="Профиль" placement="right"><IconButton aria-label="Профиль" onClick={() => setModalProfile(true)}><Avatar size="sm">{profile?.first_name?.slice(0, 1) || 'Я'}</Avatar></IconButton></Tooltip>
    </Sheet>;

    const sidebarContent = (
        <Sheet
            className="Sidebar"
            sx={{
                position: { xs: "fixed", md: "sticky" },
                transform: {
                    xs: "translateX(calc(110% * (var(--SideNavigation-slideIn, 0) - 1)))",
                    md: "none",
                },
                transition: sidebarOpen
                    ? "transform 0.35s cubic-bezier(0.4,0,0.2,1)"
                    : "transform 0.35s cubic-bezier(0.4,0,0.2,1) 0.15s",
                top: 0,
                height: { xs: '-webkit-fill-available', md: 'auto' },
                width: "var(--Sidebar-width, 240px)",
                display: "flex",
                flexDirection: "column",
                
                backdropFilter: "blur(4px)",
                borderRadius: 20,
                // borderRight: "1px solid",
                // borderColor: "divider",
                bgcolor: "var(--ld-sidebar)",

                zIndex: 100,
                py: 1,
                px: 1,
                m: 1,
                flexShrink: 0,
                overflow: "hidden auto",
            }}
        >
            {/* ── Logo / Brand ── */}
            <Box sx={{ m: 1, mb: 3}}>
                <Stack direction="row" alignItems="center" spacing={1.2}>
                    <BrandLogo />
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 800, fontSize: "0.95rem", lineHeight: 1.1,
                                letterSpacing: "-0.3px", color: "text.primary",
                            }}
                        >
                            LunaDesk
                        </Typography>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", lineHeight: 1 }}>
                            Персональный помощник
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* ── Main navigation ── */}
            <Box sx={{ backgroundColor: "neutral.50", boxShadow: 'md', p: 2, borderRadius: 15 }}>
                <Typography
                    level="body-xs"
                    sx={{ px: 0.5, mb: 0.75, color: "text.tertiary", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", fontSize: "0.65rem" }}
                >
                    Навигация
                </Typography>
                <List size="sm" sx={{ gap: 0.25, "--ListItem-paddingY": "0", "--List-gap": "2px" }}>
                    {NAV_MAIN.map(item => (
                        <NavItem
                            key={item.path}
                            item={item}
                            active={location.pathname === item.path}
                            onClick={() => handleNav(item.path)}
                        />
                    ))}
                </List>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ backgroundColor: "neutral.50", boxShadow: 'md', p: 2, borderRadius: 15 }} data-navigation="applications">
                <Typography level="body-xs" sx={{ px: .5, mb: .75, color: "text.tertiary", fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase", fontSize: ".65rem" }}>Приложения</Typography>
                <List size="sm" sx={{ gap: .25, "--ListItem-paddingY": "0" }}>
                    {NAV_APPS.map(item => <NavItem key={item.path} item={item} active={location.pathname.startsWith(item.path)} onClick={() => handleNav(item.path)}/>)}
                </List>
            </Box>
            {SHOW_TEAM && <><Divider sx={{ my: 2 }}/>
            {/* ── Secondary navigation ── */}
            <Box sx={{ backgroundColor: "neutral.50", boxShadow: 'md', p: 2, borderRadius: 15 }}>
                <Typography
                    level="body-xs"
                    sx={{ px: 0.5, mb: 0.75, color: "text.tertiary", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", fontSize: "0.65rem" }}
                >
                    Команда
                </Typography>
                <List size="sm" sx={{ gap: 0.25, "--ListItem-paddingY": "0", "--List-gap": "2px" }}>
                    {NAV_SECONDARY.map(item => (
                        <NavItem
                            key={item.path}
                            item={item}
                            active={location.pathname === item.path}
                            onClick={() => handleNav(item.path)}
                            badgeCount={item.badge ? totalUnread : 0}
                        />
                    ))}
                </List>
            </Box>

            </>}
            {/* ── Spacer ── */}
            <Box sx={{ flex: 1 }} />

            {/* ── Support button ── */}
            {/* <ListItem sx={{ p: 0, mb: 1 }}>
                <ListItemButton
                    onClick={() => setModalTicketSupport?.(true)}
                    sx={{
                        borderRadius: "10px", px: 1.5, py: 0.9, gap: 1.5,
                        bgcolor: "success.softBg", color: "success.plainColor",
                        "&:hover": { bgcolor: "success.softHoverBg" },
                        "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                >
                    <ListItemDecorator sx={{ minWidth: 0, mr: 0 }}>
                        <ContactSupportRoundedIcon />
                    </ListItemDecorator>
                    <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: 600, color: "inherit" }}>
                            Тех. Поддержка
                        </Typography>
                    </ListItemContent>
                </ListItemButton>
            </ListItem> */}

            <Divider sx={{ mb: 1 }} />

            {/* ── User profile ── */}
            <Box
                onClick={() => setModalProfile(true)}
                sx={{
                    display: "flex", gap: 1.2, alignItems: "center",
                    px: 1, py: 0.75, mb: 0.5, borderRadius: "10px",
                    cursor: "pointer", transition: "background 0.15s",
                    "&:hover": { bgcolor: "neutral.softBg" },
                }}
            >
                <Avatar
                    variant="soft"
                    color="primary"
                    size="sm"
                    src={profile?.avatar ? (/^https?:\/\//i.test(profile.avatar) ? profile.avatar : `${BASE_URL}${profile.avatar}`) : undefined}
                    sx={{ flexShrink: 0 }}
                >
                    {profile?.first_name?.[0] || "U"}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="body-sm" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {profile ? `${profile.first_name} ${profile.last_name}` : "Пользователь"}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary", lineHeight: 1 }} noWrap>
                        {profile?.email || ""}
                    </Typography>
                </Box>
                <IconButton
                    onClick={(e) => { e.stopPropagation(); logout(); }}
                    size="sm" variant="plain" color="neutral"
                    sx={{ flexShrink: 0, opacity: 0.5, "&:hover": { opacity: 1, color: "danger.plainColor" } }}
                >
                    <ExitToAppRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
            </Box>
        </Sheet>
    );

    return (
        <>
            {/* Overlay for mobile */}
            <AnimatePresence>
                {sidebarOpen && (
                    <motion.div
                        key="overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={clsBar}
                        style={{
                            position: "fixed", inset: 0,
                            zIndex: 99,
                            backdropFilter: "blur(4px)",
                            backgroundColor: "rgba(0,0,0,0.25)",
                        }}
                    />
                )}
            </AnimatePresence>

            {sidebarContent}

            {/* Кнопка закрытия — выезжает из-за правого края сайдбара */}
            <AnimatePresence>
                {sidebarOpen && (
                    <motion.div
                        key="sidebar-close-btn"
                        initial={{ x: -44, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -44, opacity: 0 }}
                        transition={{
                            duration: 0.25,
                            delay: 0.22,
                            ease: [0.34, 1.56, 0.64, 1],
                        }}
                        style={{
                            position: "fixed",
                            top: 20,
                            left: 248,   // ширина сайдбара 240 + margin 8
                            zIndex: 200,
                            display: "flex",
                        }}
                    >
                        <Box
                            onClick={clsBar}
                            sx={{
                                width: 36, height: 36,
                                borderRadius: "50%",
                                bgcolor: "var(--ld-sidebar)",
                                backdropFilter: "blur(4px)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: "pointer",
                                color: "text.tertiary",
                                transition: "color 0.15s",
                                "&:hover": { color: "danger.plainColor" },
                                ml: "8px",
                            }}
                        >
                            <CloseRoundedIcon sx={{ fontSize: 17 }} />
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
